import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Container,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { searchHotelsByLocation } from "../Utils/hotelSearch";
import Loader from "./Loader";
import { tokens } from "../theme/tokens";

const PropertyCard = ({ image, title, subtitle, onClick }) => (
  <Card
    onClick={onClick}
    elevation={0}
    sx={{
      borderRadius: `${tokens.radiusLg}px`,
      position: "relative",
      overflow: "hidden",
      height: { xs: 240, sm: 280 },
      cursor: "pointer",
      border: `1px solid ${tokens.border}`,
      boxShadow: tokens.shadowSm,
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: tokens.shadowLg,
      },
    }}
  >
    <CardMedia
      component="img"
      height="280"
      image={image}
      alt={title}
      sx={{
        objectFit: "cover",
        height: "100%",
        transform: "scale(1.02)",
        transition: "transform 0.4s ease",
        ".MuiCard-root:hover &": { transform: "scale(1.06)" },
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(180deg, transparent 35%, rgba(10,30,64,0.92) 100%)",
      }}
    />
    <CardContent
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        p: 2.5,
        "&:last-child": { pb: 2.5 },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#fff",
          fontWeight: 700,
          mb: 0.5,
          fontFamily: tokens.fontDisplay,
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </Typography>
      <Chip
        label={subtitle}
        size="small"
        sx={{
          height: 26,
          fontWeight: 600,
          fontSize: "0.7rem",
          bgcolor: "rgba(255,255,255,0.18)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.28)",
        }}
      />
    </CardContent>
  </Card>
);

const DiscoverSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const properties = [
    {
      title: "Dubai",
      subtitle: "Business hub",
      image:
        "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=800&q=80",
      locationId: 213394,
    },
    {
      title: "Mumbai",
      subtitle: "Coastal metro",
      image:
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80",
      locationId: 357389,
    },
    {
      title: "Delhi NCR",
      subtitle: "Capital corridor",
      image:
        "https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=800&q=80",
      locationId: 227760,
    },
    {
      title: "Bangalore",
      subtitle: "Tech & innovation",
      image:
        "https://images.unsplash.com/photo-1596178065887-1198b6148b2b?auto=format&fit=crop&w=800&q=80",
      locationId: 312030,
    },
    {
      title: "Pune",
      subtitle: "Growing enterprise",
      image:
        "https://images.unsplash.com/photo-1686543918113-69f8a94b3b67?auto=format&fit=crop&w=800&q=80",
      locationId: 333627,
    },
    {
      title: "Maldives",
      subtitle: "Executive retreats",
      image:
        "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=800&q=80",
      locationId: 227609,
    },
    {
      title: "Singapore",
      subtitle: "APAC gateway",
      image:
        "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=800&q=80",
      locationId: 246673,
    },
    {
      title: "Thailand",
      subtitle: "Regional travel",
      image:
        "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=800&q=80",
      locationId: null,
    },
  ];

  const handleCityHotelClick = async (locationId, locationName) => {
    if (!locationId) return;
    setLoading(true);
    try {
      await searchHotelsByLocation({ locationId, locationName, navigate });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: tokens.surface, borderTop: `1px solid ${tokens.border}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 }, maxWidth: 720, mx: "auto" }}>
            <Typography
              variant="overline"
              sx={{
                color: tokens.accent,
                fontWeight: 700,
                letterSpacing: "0.14em",
                display: "block",
                mb: 1,
              }}
            >
              Popular markets
            </Typography>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: tokens.text,
                fontFamily: tokens.fontDisplay,
                letterSpacing: "-0.02em",
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              Destinations your teams book most
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: tokens.textMuted,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                lineHeight: 1.7,
              }}
            >
              Jump straight into live availability—same search, filters, and checkout you use everywhere
              on the platform.
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {properties.map((property, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <PropertyCard
                  title={property.title}
                  subtitle={property.subtitle}
                  image={property.image}
                  onClick={() => handleCityHotelClick(property.locationId, property.title)}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default DiscoverSection;
